import ScButton from "@/src/components/ui-kit/button";
import { useTheme } from "@/src/styles/theme-provider";
import { AddCircle, CloseCircle } from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, TextInput, View } from "react-native";

type GuestEntryModalProps = Readonly<{
  visible: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  /** Confirm with the guest names entered — an empty array means "just me". */
  onConfirm: (guestNames: string[]) => void;
}>;

/**
 * Collects guest names while joining an event.
 *
 * Name only, by design — no email, no per-guest RSVP. Guests are not platform
 * users and deliberately do not consume event capacity; they exist so the
 * organiser knows the real head count.
 */
export const GuestEntryModal = ({
  visible,
  isSubmitting = false,
  onCancel,
  onConfirm,
}: GuestEntryModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [guests, setGuests] = useState<string[]>([]);
  const [draft, setDraft] = useState("");

  const addDraft = () => {
    const name = draft.trim();
    if (!name) return;
    setGuests((prev) => [...prev, name]);
    setDraft("");
  };

  const reset = () => {
    setGuests([]);
    setDraft("");
  };

  const confirm = () => {
    // Fold a half-typed name in rather than silently dropping it — losing a
    // guest because the user didn't press "add" is the obvious trap here.
    const pending = draft.trim();
    const all = pending ? [...guests, pending] : guests;
    reset();
    onConfirm(all);
  };

  const cancel = () => {
    reset();
    onCancel();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={cancel}>
      {/* varStyle carries the theme CSS variables. Without it `bg-chalk` and the
          other token classes resolve to nothing and the card renders with no
          background at all — the same pattern every other modal here uses. */}
      <View
        style={varStyle}
        className="flex-1 items-center justify-center bg-shadow-dark px-6"
      >
        <View className="w-full max-w-md rounded-2xl bg-chalk p-5 gap-4">
          <Text className="text-base font-bold text-onyx">
            {t("LABEL_BRING_GUESTS")}
          </Text>
          <Text className="text-sm text-ash-1">
            {t("HINT_BRING_GUESTS")}
          </Text>

          <View className="flex-row items-center gap-2">
            <TextInput
              value={draft}
              onChangeText={setDraft}
              onSubmitEditing={addDraft}
              placeholder={t("HINT_GUEST_NAME")}
              placeholderTextColor={themeVars["--color-ash-2"] as string}
              className="flex-1 rounded-xl border border-ash-3 px-3 py-2 text-onyx"
              returnKeyType="done"
            />
            <Pressable
              onPress={addDraft}
              disabled={!draft.trim()}
              className={`p-2 ${draft.trim() ? "" : "opacity-40"}`}
              accessibilityLabel={t("BTN_ADD")}
            >
              <AddCircle
                size={24}
                color={themeVars["--color-primary-default"] as string}
              />
            </Pressable>
          </View>

          {guests.length > 0 && (
            <View className="gap-2">
              {guests.map((name, index) => (
                <View
                  key={`${name}-${index}`}
                  className="flex-row items-center justify-between rounded-xl bg-ash-3 px-3 py-2"
                >
                  <Text className="text-sm text-onyx">{name}</Text>
                  <Pressable
                    onPress={() =>
                      setGuests((prev) => prev.filter((_, i) => i !== index))
                    }
                    accessibilityLabel={t("BTN_REMOVE")}
                  >
                    <CloseCircle
                      size={18}
                      color={themeVars["--color-slate-default"] as string}
                    />
                  </Pressable>
                </View>
              ))}
              <Text className="text-xs text-ash-1">
                {t("LABEL_GUEST_COUNT", { count: guests.length })}
              </Text>
            </View>
          )}

          <View className="flex-row justify-end gap-3 pt-1">
            <ScButton text="BTN_CANCEL" mode="outlined" onPress={cancel} />
            <ScButton
              text="BTN_JOIN"
              onPress={confirm}
              loading={isSubmitting}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default GuestEntryModal;
